import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../../../contexts/config-context';
import ListingAPI from '../../../../axios/listing';
import { MessageIcon } from '../../../../components/shared-components/message-icon';
import { formatNumber } from '../../../main-page/home-utils';
import { MessageModal } from '../../../search-page/components/message-modal';
import { listingCover } from '../../../search-page/components/result-card';
import { PanelIcon } from '../../user-panel';
import { PanelConfirm, PanelPagination } from '../panel-ui';

const SORT = '-date_created';
const BASIC_GROUP = 1;
const PROMOTED_GROUP = 2;
const DAY_MS = 24 * 60 * 60 * 1000;
// under this many days left, the expiry is shown as a warning
const EXPIRY_WARNING_DAYS = 7;
// how long an unfollowed listing can still be brought back before it leaves the list
const UNDO_MS = 5000;

// "1 dan, 21 dan" but "2 dana, 11 dana" in Serbian; "1 day" but "2 days" in English
const daysKey = (n, lang) => (lang === 'sr' ? n % 10 === 1 && n % 100 !== 11 : n === 1) ? 'days_one' : 'days_other';

const SOURCES = {
    active: { request: ListingAPI.getActiveListings, icon: 'active', cta: '/postavi-oglas' },
    inactive: { request: ListingAPI.getInactiveListings, icon: 'inactive' },
    favorite: { request: ListingAPI.getFavoriteListings, icon: 'heart', cta: '/prodaja' },
};

// bringing an inactive listing back: as it was, or as premium. Premium pays for itself only,
// since the server activates a promoted listing for free until the promotion ends
const PlanChoice = ({ expired, basicPrice, basic, promotion, initial, onChoose, onClose }) => {
    const { t, i18n } = useTranslation(["profile", "common"]);
    const [plan, setPlan] = useState(initial);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const days = (n) => n ? t(daysKey(n, i18n.language), { n }) : null;
    const cost = (price) => price > 0 ? `${formatNumber(price)} ${t('credits_short')}` : t('plan_free');
    const plans = [
        { id: 'basic', title: t('plan_basic'), text: t('plan_basic_text'), days: days(basic?.duration), price: basicPrice },
        { id: 'premium', title: t('plan_premium'), text: t('plan_premium_text'), days: days(promotion.duration), price: Number(promotion.price) || 0 },
    ];
    const chosen = plans.find(p => p.id === plan);
    const title = t(expired ? 'plan_title_renew' : 'plan_title_activate');

    return createPortal(
        <div className='hs hs-panel-confirm-overlay' onClick={onClose}>
            <div className='hs-panel-confirm hs-panel-plans' role='dialog' aria-label={title} onClick={(e) => e.stopPropagation()}>
                <h2>{title}</h2>
                <div className='hs-panel-plan-list' role='radiogroup'>
                    {plans.map(p => (
                        <button key={p.id} role='radio' aria-checked={p.id === plan} className={`hs-panel-plan ${p.id} ${p.id === plan ? 'on' : ''}`} onClick={() => setPlan(p.id)}>
                            <span className='hs-panel-plan-radio'></span>
                            <span className='hs-panel-plan-text'>
                                <span className='hs-panel-plan-top'>
                                    <span className='hs-panel-plan-title'>
                                        {p.title}
                                        {p.id === 'premium' && <span className='hs-panel-badge small orange'>{t('plan_premium_badge')}</span>}
                                    </span>
                                    <span className='hs-panel-plan-price'>{cost(p.price)}</span>
                                </span>
                                <span className='hs-panel-plan-sub'>{[p.text, p.days].filter(Boolean).join(' · ')}</span>
                            </span>
                        </button>
                    ))}
                </div>
                <div className='hs-panel-confirm-actions'>
                    <button className='hs-panel-outline' onClick={onClose}>{t('cancel', { ns: 'common' })}</button>
                    <button autoFocus className='hs-panel-primary' onClick={() => { onClose(); onChoose(plan); }}>
                        {t(expired ? 'renew' : 'activate')} · {cost(chosen.price)}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const ListingRow = ({ listing, source, promotion, basic, onMessage, onRemoved }) => {
    const { api, LISTING_GROUP } = useContext(ConfigContext);
    const { t, i18n } = useTranslation(["profile", "listing", "common"]);
    const navigate = useNavigate();
    const menuRef = useRef(null);

    const owned = source !== 'favorite';
    const [active, setActive] = useState(source !== 'inactive');
    const [promoted, setPromoted] = useState(listing.promoted);
    const [likes, setLikes] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    // { message, label, danger, onConfirm }
    const [confirm, setConfirm] = useState(null);
    // { ok, text }
    const [message, setMessage] = useState(null);
    // the plan the choice dialog opens on, while it is open
    const [planChoice, setPlanChoice] = useState(null);
    // unfollowed, waiting a moment for an undo before leaving the list
    const [unfollowed, setUnfollowed] = useState(false);

    useEffect(() => {
        if (!unfollowed) return;
        const timer = setTimeout(() => onRemoved(listing.id), UNDO_MS);
        return () => clearTimeout(timer);
    }, [unfollowed]);

    useEffect(() => {
        ListingAPI.countLikes(listing.id).then(res => setLikes(res.data.count)).catch(() => { });
    }, [listing.id]);

    useEffect(() => {
        const close = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    const expired = new Date(listing.date_active) < new Date();
    const price = parseFloat(listing.price) || 0;
    const fail = (text) => setMessage({ ok: false, text });
    const paidFail = (fallback) => (err) => fail(t(err.response?.status === 402 ? 'low_funds' : fallback, { ns: 'listing' }));

    const activate = () => {
        setMessage(null);
        ListingAPI.activateListing(listing.id).then(() => setActive(true)).catch(paidFail('activate_error'));
    };

    const renew = () => {
        setMessage(null);
        ListingAPI.renewListing(listing.id).then(activate).catch(() => fail(t('renewal_error', { ns: 'listing' })));
    };

    const deactivate = () => {
        setMessage(null);
        ListingAPI.deactivateListing(listing.id).then(() => setActive(false)).catch(() => fail(t('deactivate_error', { ns: 'listing' })));
    };

    const promote = () => {
        ListingAPI.promoteListing({ listing: listing.id, listing_group: LISTING_GROUP.PROMOTED }).then(() => {
            setPromoted(true);
            setMessage({ ok: true, text: t('promotion_success', { ns: 'listing' }) });
        }).catch(paidFail('promotion_error'));
    };

    const remove = () => {
        ListingAPI.deleteListing(listing.id).then(() => onRemoved(listing.id)).catch(() => fail(t('delete_error', { ns: 'listing' })));
    };

    const unfollow = () => {
        setMessage(null);
        ListingAPI.unlikeListing(listing.id).then(() => setUnfollowed(true)).catch(() => fail(t('unfollow_error')));
    };

    const refollow = () => {
        ListingAPI.likeListing(listing.id).then(() => setUnfollowed(false)).catch(() => { });
    };

    const askPromote = () => setConfirm({
        message: `${t('promotion_price_message_1', { ns: 'listing' })}${promotion.duration}${t('promotion_price_message_2', { ns: 'listing' })}${promotion.price}${t('promotion_price_message_3', { ns: 'listing' })}`,
        label: t('promote_listing', { ns: 'listing' }),
        onConfirm: promote,
    });
    // the inactive list says what activating costs; a paid one is confirmed first
    const activationPrice = Number(listing.activation_price) || 0;
    const reactivate = expired ? renew : activate;
    const confirmActivate = () => activationPrice > 0
        ? setConfirm({
            message: t('activation_cost_confirm', { price: formatNumber(activationPrice) }),
            label: t(expired ? 'renew' : 'activate'),
            onConfirm: reactivate,
        })
        : reactivate();

    // premium is paid as a promotion; activating afterwards is free while it runs, so no renewal first
    const activatePremium = () => {
        setMessage(null);
        ListingAPI.promoteListing({ listing: listing.id, listing_group: LISTING_GROUP.PROMOTED })
            .then(() => {
                setPromoted(true);
                return ListingAPI.activateListing(listing.id);
            })
            .then(() => setActive(true))
            .catch(paidFail('promotion_error'));
    };

    // an inactive listing that is not premium yet gets the choice between the two
    const canChoosePlan = owned && !active && !promoted && promotion && basic;
    const askActivate = () => canChoosePlan ? setPlanChoice('basic') : confirmActivate();
    const askDelete = () => setConfirm({ message: t('delete_confirm_message', { ns: 'listing' }), label: t('delete_listing', { ns: 'listing' }), danger: true, onConfirm: remove });
    const edit = () => navigate(`/postavi-oglas/azuriranje/${listing.id}`);

    const menu = owned ? [
        !active && { label: t('edit_listing', { ns: 'listing' }), onClick: edit },
        // promoting an inactive listing also brings it back, so it goes through the choice
        !promoted && promotion && { label: t('promote_listing', { ns: 'listing' }), onClick: canChoosePlan ? () => setPlanChoice('premium') : askPromote },
        { label: t('statistics', { ns: 'listing' }), onClick: () => navigate(`/korisnik/statistika/${listing.id}`) },
        active && { label: t('deactivate_listing', { ns: 'listing' }), onClick: deactivate },
        { label: t('delete_listing', { ns: 'listing' }), onClick: askDelete, danger: true },
    ].filter(Boolean) : [];

    // a followed listing the owner took down, deleted or let expire has no page to open any more
    const unavailable = !owned && (listing.is_active === false || listing.is_deleted || expired);
    // inactive listings have no public page to open
    const viewable = owned ? active : !unavailable;
    const href = `/oglas/${listing.id}`;
    const cover = { backgroundImage: `url(${listingCover(listing, api)})` };

    // a badge only says what the page does not: expired among the inactive, inactive among the active
    const status = unavailable ? { label: t('status_unavailable'), className: 'gray' }
        : !owned || active ? null
        : expired ? { label: t('status_expired'), className: 'gray' }
            : source === 'active' ? { label: t('status_inactive'), className: 'gray' }
                : null;
    // an owner cares when the listing runs out more than when it was posted
    const daysLeft = owned && !expired ? Math.max(0, Math.ceil((new Date(listing.date_active) - new Date()) / DAY_MS)) : null;
    const dateFormat = i18n.language === 'sr' ? 'sr-RS' : 'en-GB';
    const hasPhoto = listing.photos?.length > 0;

    if (unfollowed) {
        return (
            <div className='hs-panel-listing hs-panel-listing-undo'>
                <span>{t('unfollowed')}</span>
                <button className='hs-panel-text-btn' onClick={refollow}>{t('undo')}</button>
            </div>
        );
    }

    return (
        <div className={`hs-panel-listing ${unavailable ? 'unavailable' : ''}`}>
            {viewable
                ? <Link to={href} className={`hs-panel-listing-photo ${hasPhoto ? '' : 'empty'}`} style={hasPhoto ? cover : undefined} aria-label={listing.title}>
                    {!hasPhoto && <PanelIcon name='home' size={26} />}
                </Link>
                : <div className={`hs-panel-listing-photo ${hasPhoto ? '' : 'empty'}`} style={hasPhoto ? cover : undefined}>
                    {!hasPhoto && <PanelIcon name='home' size={26} />}
                </div>}
            <div className='hs-panel-listing-info'>
                <span className='hs-panel-listing-title'>
                    {viewable ? <Link to={href} className='hs-panel-listing-link'>{listing.title}</Link> : <span>{listing.title}</span>}
                    {status && <span className={`hs-panel-badge small ${status.className}`}>{status.label}</span>}
                    {promoted && <span className='hs-panel-badge small orange'>{listing.promoted_value?.label || 'Premium'}</span>}
                </span>
                <span className='hs-panel-listing-loc'>{[listing.place?.label, listing.subplace?.label, listing.location?.label].filter(Boolean).join(' · ')}</span>
                <span className='hs-panel-listing-price'>{price > 0 ? `${formatNumber(price)} €` : t('price_placeholder', { ns: 'listing' })}</span>
                {message && <span className={`hs-panel-status ${message.ok ? 'ok' : 'err'}`}>{message.text}</span>}
            </div>
            <div className='hs-panel-listing-stats'>
                <span className='saves'><strong>{likes !== null ? formatNumber(likes) : '–'}</strong>{t('saves')}</span>
                {daysLeft !== null
                    ? <span className={`when ${daysLeft < EXPIRY_WARNING_DAYS ? 'warn' : ''}`}>
                        <strong>{daysLeft === 0 ? t('expires_today') : t(daysKey(daysLeft, i18n.language), { n: daysLeft })}</strong>{t('until_expiry')}
                    </span>
                    : owned
                        ? <span className='when'><strong>{new Date(listing.date_active).toLocaleDateString(dateFormat)}</strong>{t('expired_on')}</span>
                        : <span className='when'><strong>{new Date(listing.date_created).toLocaleDateString(dateFormat)}</strong>{t('published')}</span>}
            </div>
            <div className='hs-panel-listing-actions'>
                {/* the photo and the title open it too; the button stays for phones */}
                {viewable && <Link to={href} className='hs-panel-outline hs-panel-listing-view'>{t('view')}</Link>}
                {!owned && !unavailable && <button className='hs-panel-soft' onClick={() => onMessage(listing)}><MessageIcon size={16} />{t('message')}</button>}
                {/* the same orange heart as everywhere else; a tap lets go of the listing */}
                {!owned &&
                    <button className='hs-panel-outline icon hs-panel-unfollow' onClick={unfollow} aria-label={t('unfollow')} title={t('unfollow')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#FF5722" stroke="#FF5722" strokeWidth="2"><path d="M12 20.5s-7.5-4.6-9.3-9.3C1.4 7.8 3.6 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.4 0 5.6 3.3 4.3 6.7-1.8 4.7-9.3 9.3-9.3 9.3Z" /></svg>
                    </button>}
                {owned && active && <button className='hs-panel-soft' onClick={edit}>{t('edit')}</button>}
                {owned && !active &&
                    <button className='hs-panel-soft' onClick={askActivate}>{t(expired ? 'renew' : 'activate')}</button>}
                {owned && (
                    <div className='hs-panel-more' ref={menuRef}>
                        <button className='hs-panel-outline icon' aria-label={t('more')} onClick={() => setMenuOpen(!menuOpen)}>⋯</button>
                        {menuOpen && (
                            <div className='hs-panel-pop hs-panel-more-pop'>
                                {menu.map(item => (
                                    <button key={item.label} className={`hs-panel-pop-item ${item.danger ? 'danger' : ''}`}
                                        onClick={() => { setMenuOpen(false); item.onClick(); }}>
                                        <span className='hs-panel-pop-label'>{item.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {planChoice && <PlanChoice expired={expired} basicPrice={activationPrice} basic={basic} promotion={promotion} initial={planChoice}
                onChoose={(plan) => plan === 'premium' ? activatePremium() : reactivate()} onClose={() => setPlanChoice(null)} />}
            {confirm && <PanelConfirm danger={confirm.danger} title={t('attention', { ns: 'common' })} message={confirm.message} confirmLabel={confirm.label}
                onClose={() => setConfirm(null)} onConfirm={confirm.onConfirm} />}
        </div>
    );
};

export const PanelListings = ({ source }) => {
    const { t, i18n } = useTranslation(["profile"]);
    const lang = i18n.language;
    const { request, icon, cta } = SOURCES[source];

    const [listings, setListings] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [promotion, setPromotion] = useState(null);
    const [basic, setBasic] = useState(null);
    const [messageListing, setMessageListing] = useState(null);

    useEffect(() => {
        let cancelled = false;
        request(page, SORT, lang).then(res => {
            if (cancelled) return;
            setListings(res.data.results);
            setPageCount(res.data.page_count);
            setLoaded(true);
        }).catch(err => console.error("Error fetching listings:", err));
        return () => { cancelled = true; };
    }, [request, page, lang]);

    useEffect(() => {
        if (source === 'favorite') return;
        ListingAPI.getPromotionPrice(PROMOTED_GROUP).then(res => setPromotion(res.data)).catch(console.error);
        if (source === 'inactive') ListingAPI.getPromotionPrice(BASIC_GROUP).then(res => setBasic(res.data)).catch(console.error);
    }, [source]);

    if (!loaded) return null;

    if (listings.length === 0) {
        return (
            <section className='hs hs-panel-card hs-panel-empty'>
                <span className='hs-panel-empty-icon'><PanelIcon name={icon} size={24} /></span>
                <h2>{t(`empty_${source}_title`)}</h2>
                <p>{t(`empty_${source}_text`)}</p>
                {cta && <Link to={cta} className='hs-panel-primary'>{t(`empty_${source}_cta`)}</Link>}
            </section>
        );
    }

    return (
        <section className='hs hs-panel-page'>
            <div className={`hs-panel-card hs-panel-listings ${source}`}>
                {listings.map(listing => (
                    <ListingRow key={listing.id} listing={listing} source={source} promotion={promotion} basic={basic}
                        onMessage={setMessageListing}
                        onRemoved={(id) => setListings(listings.filter(l => l.id !== id))} />
                ))}
            </div>
            <PanelPagination page={page} pageCount={pageCount} onChange={setPage} />
            {messageListing && <MessageModal listing={messageListing} onClose={() => setMessageListing(null)} />}
        </section>
    );
};
